/**
 * Skeleton Component
 * Base loading skeleton with shimmer animation
 *
 * Provides a composable primitive for creating loading states that prevent
 * Cumulative Layout Shift (CLS) by matching the dimensions of actual content.
 *
 * Features:
 * - Shimmer animation (respects prefers-reduced-motion)
 * - Composable design for flexible layouts
 * - Accessibility attributes included
 */

import * as React from 'react';
import { cn } from '../../lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional CSS classes to apply
   */
  className?: string;
  /**
   * Whether to show shimmer animation (default: true)
   */
  shimmer?: boolean;
}

/**
 * Base Skeleton primitive
 * Use this to create loading placeholders that match your content dimensions
 */
export function Skeleton({ className, shimmer = true, ...props }: SkeletonProps) {
  return (
    <div
      className={cn('bg-neutral-100 rounded relative overflow-hidden', className)}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading content"
      {...props}
    >
      {shimmer && (
        <div
          className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/60 to-transparent motion-safe:animate-shimmer"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Number of text lines to show (default: 1)
   */
  lines?: number;
  /**
   * Additional CSS classes to apply to container
   */
  className?: string;
}

/**
 * SkeletonText - Multi-line text placeholder
 * Last line is automatically 75% width for natural text appearance
 */
export function SkeletonText({ lines = 1, className, ...props }: SkeletonTextProps) {
  return (
    <div className={className} role="status" aria-busy="true" aria-live="polite" {...props}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-4 mb-2', i === lines - 1 ? 'w-3/4' : 'w-full')}
        />
      ))}
    </div>
  );
}

interface SkeletonCardProps {
  /**
   * Whether to include an image placeholder at the top
   */
  showImage?: boolean;
  /**
   * Additional CSS classes to apply to card container
   */
  className?: string;
}

/**
 * SkeletonCard - Common card layout with optional image
 * Useful for grid layouts like service cards, product cards, etc.
 */
export function SkeletonCard({ showImage = false, className }: SkeletonCardProps) {
  return (
    <div className={cn('bg-white border-2 border-neutral-200 rounded-lg overflow-hidden', className)}>
      {showImage && <Skeleton className="h-32 w-full rounded-none" />}
      <div className="p-6">
        <Skeleton className="h-8 w-32 mb-2" />
        <SkeletonText lines={2} className="mb-4" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  );
}
